import React, { useState } from "react";
import { IoClose, IoPhonePortraitOutline } from "react-icons/io5";

import useMatchMedia from "../../hooks/useMatchMedia";
import {
  APP_VIEW_PHONE_QUERY,
  appViewUrl,
  dismissAppViewBanner,
  isAppViewBannerDismissed,
} from "../../helpers/appView";
import { useTranslation } from "react-i18next";

// The header's "Mobile view" button (tablets and desktops; phones get the
// banner and the menu entry). Opens the app view in this tab.
export const AppViewButton = () => {
  const { t } = useTranslation("common");
  return (
    <a
      href={appViewUrl()}
      title={t("appView.bannerText")}
      aria-label={t("appView.mobileView")}
      className="hidden md:flex items-center gap-1.5 rounded-full border border-ui-border px-2 py-1 text-[13px] font-medium text-ui-text-muted no-underline transition-colors hover:bg-brand-light hover:text-brand"
    >
      <IoPhonePortraitOutline className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="hidden lg:inline whitespace-nowrap">
        {t("appView.mobileView")}
      </span>
    </a>
  );
};

// The same entry in the phone menu (the drawer).
export const AppViewDrawerLink = () => {
  const { t } = useTranslation("common");
  return (
    <a
      href={appViewUrl()}
      className="mb-1 flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-ui-text-muted no-underline hover:bg-ui-surface-sunken hover:text-brand"
    >
      <IoPhonePortraitOutline className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span>{t("appView.drawer")}</span>
    </a>
  );
};

// On phone-sized screens: a small banner offering the app view. It never
// redirects; closing it is remembered in this browser.
export const AppViewBanner = () => {
  const { t } = useTranslation("common");
  const isPhone = useMatchMedia(APP_VIEW_PHONE_QUERY);
  const [dismissed, setDismissed] = useState(() => isAppViewBannerDismissed());

  if (!isPhone || dismissed) {
    return null;
  }

  const close = () => {
    dismissAppViewBanner();
    setDismissed(true);
  };

  return (
    <div
      role="region"
      aria-label={t("appView.app")}
      className="flex items-center gap-2 border-b border-ui-border bg-brand-light px-3 py-2 text-[13px] text-brand"
    >
      <IoPhonePortraitOutline className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="min-w-0 flex-1">{t("appView.bannerText")}</span>
      <a
        href={appViewUrl()}
        className="shrink-0 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-white no-underline hover:opacity-90"
      >
        {t("appView.open")}
      </a>
      <button
        type="button"
        onClick={close}
        aria-label={t("appView.dismiss")}
        className="shrink-0 rounded-md p-1 hover:bg-ui-surface-sunken"
      >
        <IoClose className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
};
